import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios, { authHeaders } from "../../utils/axios";

// Features Actions
export const fetchFeatures = createAsyncThunk(
    "subscription/fetchFeatures",
    async ({ search = "", page = 1, limit = 10 }, { rejectWithValue }) => {
        try {
            const res = await Axios.get(
                `/subscriptions/features?search=${search}&page=${page}&limit=${limit}`,
                authHeaders()
            );
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const createFeature = createAsyncThunk(
    "subscription/createFeature",
    async (data, { rejectWithValue }) => {
        try {
            const res = await Axios.post("/subscriptions/features", data, authHeaders());
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const updateFeature = createAsyncThunk(
    "subscription/updateFeature",
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const res = await Axios.put(`/subscriptions/features/${id}`, data, authHeaders());
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const deleteFeature = createAsyncThunk(
    "subscription/deleteFeature",
    async (id, { rejectWithValue }) => {
        try {
            const res = await Axios.delete(`/subscriptions/features/${id}`, authHeaders());
            return { id, res: res.data };
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const toggleFeatureStatus = createAsyncThunk(
    "subscription/toggleFeatureStatus",
    async ({ id, status }, { rejectWithValue }) => {
        try {
            const res = await Axios.patch(
                `/subscriptions/features/${id}`,
                { status },
                authHeaders()
            );
            return { id, status, res: res.data };
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

// Plans Actions
export const fetchPlans = createAsyncThunk(
    "subscription/fetchPlans",
    async ({ search = "", page = 1, limit = 10 }, { rejectWithValue }) => {
        try {
            const res = await Axios.get(
                `/subscriptions/plans?search=${search}&page=${page}&limit=${limit}`,
                authHeaders()
            );
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const fetchPlanById = createAsyncThunk(
    "subscription/fetchPlanById",
    async ({ id, include_features = false }, { rejectWithValue }) => {
        try {
            let url = `/subscriptions/plans/${id}`;
            if (include_features) {
                url += `?include_features=true`;
            }
            const res = await Axios.get(url, authHeaders());
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const createPlan = createAsyncThunk(
    "subscription/createPlan",
    async (data, { rejectWithValue }) => {
        try {
            const res = await Axios.post("/subscriptions/plans", data, authHeaders());
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const updatePlan = createAsyncThunk(
    "subscription/updatePlan",
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const res = await Axios.patch(`/subscriptions/plans/${id}`, data, authHeaders());
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const deletePlan = createAsyncThunk(
    "subscription/deletePlan",
    async (id, { rejectWithValue }) => {
        try {
            const res = await Axios.delete(`/subscriptions/plans/${id}`, authHeaders());
            return { id, res: res.data };
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const togglePlanStatus = createAsyncThunk(
    "subscription/togglePlanStatus",
    async ({ id, status }, { rejectWithValue }) => {
        try {
            const res = await Axios.patch(`/subscriptions/plans/${id}`, { status }, authHeaders());
            return { id, status, res: res.data };
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const reorderPlan = createAsyncThunk(
    "subscription/reorderPlan",
    async ({ id, order }, { rejectWithValue }) => {
        try {
            const res = await Axios.patch(
                `/subscriptions/plans/${id}/reorder`,
                { new_order: order },
                authHeaders()
            );
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

// Plan Features Mapping
export const fetchPlanFeatures = createAsyncThunk(
    "subscription/fetchPlanFeatures",
    async (planId, { rejectWithValue }) => {
        try {
            const res = await Axios.get(`/subscriptions/plans/${planId}/features`, authHeaders());
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const addFeaturesToPlan = createAsyncThunk(
    "subscription/addFeaturesToPlan",
    async ({ planId, data }, { rejectWithValue }) => {
        try {
            const res = await Axios.post(`/subscriptions/plans/${planId}/features`, data, authHeaders());
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const updatePlanFeature = createAsyncThunk(
    "subscription/updatePlanFeature",
    async ({ planId, featureId, data }, { rejectWithValue }) => {
        try {
            const res = await Axios.put(
                `/subscriptions/plans/${planId}/features/${featureId}`,
                data,
                authHeaders()
            );
            return { planId, featureId, res: res.data };
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const removePlanFeature = createAsyncThunk(
    "subscription/removePlanFeature",
    async ({ planId, featureId }, { rejectWithValue }) => {
        try {
            const res = await Axios.delete(
                `/subscriptions/plans/${planId}/features/${featureId}`,
                authHeaders()
            );
            return { featureId, res: res.data };
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const fetchSubscriptionStats = createAsyncThunk(
    "subscription/fetchSubscriptionStats",
    async (_, { rejectWithValue }) => {
        try {
            const res = await Axios.get("/subscriptions/stats", authHeaders());
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const fetchUserSubscriptions = createAsyncThunk(
    "subscription/fetchUserSubscriptions",
    async ({ page = 1, limit = 10, user_id = "", status = "", source = "", plan_ids = "", id = "" }, { rejectWithValue }) => {
        try {
            let url = `/subscriptions?page=${page}&limit=${limit}`;
            if (id) {
                url += `&id=${id}`;
            }
            if (user_id) {
                url += `&user_id=${user_id}`;
            }
            if (status) {
                url += `&status=${status}`;
            }
            if (source) {
                url += `&source=${source}`;
            }
            if (plan_ids) {
                url += `&plan_ids=${plan_ids}`;
            }
            const res = await Axios.get(url, authHeaders());
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const fetchPaymentTransactions = createAsyncThunk(
    "subscription/fetchPaymentTransactions",
    async ({ page = 1, limit = 10, search = "", status = "", subscription_id = "" }, { rejectWithValue }) => {
        try {
            let url = `/subscriptions/payment-transcations?page=${page}&limit=${limit}`;
            if (search) {
                url += `&search=${search}`;
            }
            if (status) {
                url += `&status=${status}`;
            }
            if (subscription_id) {
                url += `&subscription_id=${subscription_id}`;
            }
            const res = await Axios.get(url, authHeaders());
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const fetchPaymentTransactionStats = createAsyncThunk(
    "subscription/fetchPaymentTransactionStats",
    async (_, { rejectWithValue }) => {
        try {
            const res = await Axios.get("/subscriptions/payment-transcations/stats", authHeaders());
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const downloadPaymentInvoice = createAsyncThunk(
    "subscription/downloadPaymentInvoice",
    async ({ payment_transaction_id }, { rejectWithValue }) => {
        try {
            const res = await Axios.post(
                "/subscriptions/download-invoice",
                { payment_transaction_id },
                authHeaders()
            );
            return res.data?.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const reverseUserSubscription = createAsyncThunk(
    "subscription/reverseUserSubscription",
    async ({ subscription_id }, { rejectWithValue }) => {
        try {
            const res = await Axios.post(
                "/subscriptions/reverse",
                { subscription_id },
                authHeaders()
            );
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

const subscriptionSlice = createSlice({
    name: "subscription",
    initialState: {
        features: [],
        plans: [],
        planFeatures: [], // Features for a specific plan
        userSubscriptions: [],
        totalUserSubscriptions: 0,
        paymentTransactions: [],
        totalPaymentTransactions: 0,
        total: 0,
        isLoading: false,
        error: null,
        stats: null,
        paymentTransactionStats: null,
    },
    reducers: {
        clearSubscriptionError: (state) => {
            state.error = null;
        },
        resetPlanFeatures: (state) => {
            state.planFeatures = [];
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Subscription Stats
            .addCase(fetchSubscriptionStats.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchSubscriptionStats.fulfilled, (state, action) => {
                state.isLoading = false;
                state.stats = action.payload?.data || null;
            })
            .addCase(fetchSubscriptionStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Fetch User Subscriptions
            .addCase(fetchUserSubscriptions.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchUserSubscriptions.fulfilled, (state, action) => {
                state.isLoading = false;
                state.userSubscriptions = action.payload?.data || [];
                state.totalUserSubscriptions = action.payload?.meta?.total || 0;
            })
            .addCase(fetchUserSubscriptions.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Fetch Payment Transactions
            .addCase(fetchPaymentTransactions.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchPaymentTransactions.fulfilled, (state, action) => {
                state.isLoading = false;
                state.paymentTransactions = action.payload?.data || [];
                state.totalPaymentTransactions = action.payload?.meta?.total || 0;
            })
            .addCase(fetchPaymentTransactions.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Fetch Payment Transaction Stats
            .addCase(fetchPaymentTransactionStats.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchPaymentTransactionStats.fulfilled, (state, action) => {
                state.isLoading = false;
                state.paymentTransactionStats = action.payload?.data || null;
            })
            .addCase(fetchPaymentTransactionStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Reverse User Subscription
            .addCase(reverseUserSubscription.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(reverseUserSubscription.fulfilled, (state, action) => {
                state.isLoading = false;
                // Update revoked status locally in list if it exists
                const subscription_id = action.meta.arg.subscription_id;
                const sub = state.userSubscriptions.find(s => s.id === subscription_id);
                if (sub) {
                    sub.status = "revoked";
                }
            })
            .addCase(reverseUserSubscription.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Fetch Features
            .addCase(fetchFeatures.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchFeatures.fulfilled, (state, action) => {
                state.isLoading = false;
                state.features = action.payload?.data || [];
                state.total = action.payload?.meta?.total || 0;
            })
            .addCase(fetchFeatures.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Delete Feature
            .addCase(deleteFeature.fulfilled, (state, action) => {
                state.features = state.features.filter((f) => f.id !== action.payload.id);
                state.total -= 1;
            })

            // Toggle Feature Status
            .addCase(toggleFeatureStatus.fulfilled, (state, action) => {
                const feature = state.features.find((f) => f.id === action.payload.id);
                if (feature) {
                    feature.status = action.payload.status;
                }
            })

            // Fetch Plans
            .addCase(fetchPlans.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchPlans.fulfilled, (state, action) => {
                state.isLoading = false;
                state.plans = action.payload?.data || [];
                state.total = action.payload?.meta?.total || 0;
            })
            .addCase(fetchPlans.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Delete Plan
            .addCase(deletePlan.fulfilled, (state, action) => {
                state.plans = state.plans.filter((p) => p.id !== action.payload.id);
                state.total -= 1;
            })

            // Toggle Plan Status
            .addCase(togglePlanStatus.fulfilled, (state, action) => {
                const plan = state.plans.find((p) => p.id === action.payload.id);
                if (plan) {
                    plan.status = action.payload.status;
                }
            })

            // Fetch Plan Features
            .addCase(fetchPlanFeatures.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchPlanFeatures.fulfilled, (state, action) => {
                state.isLoading = false;
                state.planFeatures = action.payload?.data || [];
            })
            .addCase(fetchPlanFeatures.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Remove Plan Feature
            .addCase(removePlanFeature.fulfilled, (state, action) => {
                state.planFeatures = state.planFeatures.filter(
                    (pf) => pf.feature_id !== action.payload.featureId
                );
            })

            .addCase(updatePlanFeature.fulfilled, (state, action) => {
                const index = state.planFeatures.findIndex(pf => pf.feature_id === action.payload.featureId);
                if (index !== -1) {
                    state.planFeatures[index] = {
                        ...state.planFeatures[index],
                        ...action.payload.res.data
                    };
                }
            });
    },
});

export const { clearSubscriptionError, resetPlanFeatures } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
